import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * Overclock's shots (the Glitch set's gun, ADR-0160 amended): pixels, never curves. A square bolt split into magenta,
 * white and lime like a bad signal, a burst of squares out of the muzzle, a square shattering where it lands, and the
 * bit the set's knock-out deletes its gardener into. Outline-free (effects are the brightest things on screen), no team
 * keys.
 */

/** A run of `tone` on row `y`, x0..x1 (inclusive): what the bolt's trail and the streaks are made of. */
type Run = readonly [y: number, x0: number, x1: number, tone: PaletteKey];

/** Paints `runs` (later ones over earlier ones), then `squares` ([x, y, side, tone], top-left). */
function blocks(
  c: PixelCanvas,
  runs: readonly Run[],
  squares: readonly (readonly [number, number, number, PaletteKey])[] = [],
): void {
  for (const [y, x0, x1, tone] of runs) c.hline(x0, x1, y, tone);
  for (const [x, y, s, tone] of squares) for (let k = 0; k < s; k++) c.hline(x, x + s - 1, y + k, tone);
}

/** A square of side `s` at (x, y) split like a bad signal: magenta up-left, lime down-right, `core` over both. */
function split(c: PixelCanvas, x: number, y: number, s: number, core: PaletteKey = 'w'): void {
  blocks(
    c,
    [],
    [
      [x - 1, y - 1, s, 'p'],
      [x + 1, y + 1, s, 'l'],
      [x, y, s, core],
    ],
  );
}

// --- The bolt --------------------------------------------------------------------------------------------------------

/** Its trail per frame: a white band, magenta above it and lime under it, broken and offset differently each frame. */
const TRAILS: readonly (readonly Run[])[] = [
  [
    [6, 4, 8, 'p'],
    [6, 10, 16, 'p'],
    [9, 7, 12, 'l'],
    [9, 14, 17, 'l'],
    [7, 9, 17, 'w'],
    [8, 11, 17, 'w'],
    [4, 13, 15, 'p'],
    [11, 9, 10, 'l'],
  ],
  [
    [6, 2, 5, 'p'],
    [6, 7, 15, 'p'],
    [9, 5, 9, 'l'],
    [9, 11, 17, 'l'],
    [7, 7, 17, 'w'],
    [8, 10, 17, 'w'],
    [5, 15, 16, 'p'],
    [10, 3, 4, 'l'],
  ],
  [
    [6, 6, 13, 'p'],
    [6, 15, 16, 'p'],
    [9, 3, 7, 'l'],
    [9, 9, 17, 'l'],
    [7, 10, 17, 'w'],
    [8, 8, 17, 'w'],
    [11, 12, 14, 'p'],
    [4, 6, 7, 'l'],
  ],
];

const bolt = (f: number) => (c: PixelCanvas) => {
  blocks(c, TRAILS[f] ?? []);
  // Its head: a square at the pellet's size (its middle on (21, 8)), its light split.
  split(c, 18, 5, 6);
  c.set(19, 6, f === 1 ? 'l' : 'w');
};

// --- The muzzle flash ------------------------------------------------------------------------------------------------

const squareBurst = (f: number) => (c: PixelCanvas) => {
  if (f === 0) {
    // A square of light at the muzzle, streaks of signal and squares flung on from it.
    blocks(c, [
      [17, 11, 24, 'p'],
      [23, 12, 28, 'l'],
      [20, 13, 31, 'w'],
    ]);
    split(c, 5, 16, 8);
    blocks(
      c,
      [],
      [
        [17, 11, 3, 'w'],
        [21, 26, 3, 'l'],
        [26, 15, 3, 'p'],
        [15, 27, 2, 'w'],
      ],
    );
    return;
  }
  if (f === 1) {
    // The square opens into a frame of lime, the squares fly on and shrink.
    for (let k = 0; k < 12; k++) {
      c.set(4 + k, 14, 'l').set(4 + k, 25, 'l');
      c.set(4, 14 + k, 'l').set(15, 14 + k, 'l');
    }
    blocks(
      c,
      [
        [19, 20, 30, 'p'],
        [21, 24, 36, 'w'],
      ],
      [
        [8, 18, 4, 'w'],
        [25, 8, 3, 'w'],
        [29, 29, 2, 'l'],
        [34, 15, 3, 'p'],
        [22, 31, 2, 'w'],
      ],
    );
    return;
  }
  // The last few, far out and small.
  blocks(
    c,
    [[20, 34, 40, 'l']],
    [
      [31, 6, 2, 'w'],
      [36, 30, 2, 'p'],
      [41, 16, 2, 'w'],
      [9, 19, 2, 'l'],
    ],
  );
};

// --- The burst -------------------------------------------------------------------------------------------------------

/**
 * A frame of squares round the middle: side `s`, every `step` px round a square of half-side `r`, toned in turn by
 * `tones` along each side; with `gaps`, one in so many missing (a frame breaking up).
 */
function ring(c: PixelCanvas, r: number, s: number, step: number, tones: readonly PaletteKey[], gaps = 0): void {
  const half = Math.floor(s / 2);
  let k = 0;
  for (const [side, [ax, ay, dx, dy]] of [
    [-r, -r, 1, 0],
    [r, -r, 0, 1],
    [r, r, -1, 0],
    [-r, r, 0, -1],
  ].entries())
    for (let t = 0; t < 2 * r; t += step) {
      k++;
      if (gaps && (k * 7 + side * 3) % gaps === 0) continue;
      const [x, y] = [22 + (ax as number) + (dx as number) * t, 22 + (ay as number) + (dy as number) * t];
      blocks(c, [], [[x - half, y - half, s, tones[k % tones.length] as PaletteKey]]);
    }
}

const shatter = (f: number) => (c: PixelCanvas) => {
  if (f === 0) {
    split(c, 17, 17, 10);
    return;
  }
  if (f === 1) {
    ring(c, 9, 3, 3, ['w', 'w', 'l', 'w', 'p']);
    split(c, 20, 20, 4, 'l');
    return;
  }
  if (f === 2) {
    ring(c, 14, 3, 7, ['w', 'p', 'l'], 4);
    return;
  }
  ring(c, 19, 2, 19, ['w', 'l', 'p', 'w']);
  ring(c, 16, 2, 8, ['l', 'p'], 2);
};

// --- The bit ---------------------------------------------------------------------------------------------------------

/** One deleted pixel blinking out: white, lime, a stutter aside, magenta, smaller and smaller. */
const BIT: readonly (readonly [x: number, y: number, s: number, tone: PaletteKey])[] = [
  [4, 4, 4, 'w'],
  [4, 4, 4, 'l'],
  [5, 4, 4, 'w'],
  [4, 4, 4, 'p'],
  [5, 5, 2, 'w'],
  [5, 5, 2, 'l'],
];

const bit = (f: number) => (c: PixelCanvas) => {
  const [x, y, s, tone] = BIT[f] as (typeof BIT)[number];
  paint(c, (px, py): Tone => (px >= x && px < x + s && py >= y && py < y + s ? tone : null));
};

export default [
  sprite({
    name: 'overclock_shot',
    ...shot,
    notes: `Overclock's bullet (ADR-0160 amended): a square of white light at the pellet's size, split like a bad signal
(magenta up-left, lime down-right), its trail three broken bands (magenta, white, lime) shifting each frame. Head right.
Rubric (4×): S5 R5 P5 L4 C4 A4 F4. Weakest: on the lawn its lime band is low in contrast (the white and its glow carry it).`,
    anims: { idle: { fps: 15, frames: frames3(bolt) } },
  }),
  sprite({
    name: 'overclock_muzzle',
    ...muzzle,
    notes: `Overclock's muzzle flash (ADR-0160 amended): a square of split light at the muzzle with streaks of signal and
squares flung on, opening into a lime frame, the squares flying on smaller, the last few far out. Points right.
Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(squareBurst) } },
  }),
  sprite({
    name: 'overclock_burst',
    ...burst,
    notes: `Overclock's hit (ADR-0160 amended): a square of split light shattering into a frame of squares that breaks up
and flies apart, the last bits far out. 4 frames. Rubric (4×): S5 R4 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(shatter) } },
  }),
  sprite({
    name: 'glitch_bit',
    group: 'fx',
    size: [12, 12],
    origin: [6, 6],
    density: 4,
    notes: `A deleted pixel (the Glitch set's knock-out, ADR-0160 amended): a 4-px block blinking white, lime, a stutter
aside, magenta, then smaller. Rubric (4×): S4 R4 P5 L4 C5 A4 F4.`,
    anims: { idle: { fps: 12, frames: BIT.map((_, f) => bit(f)) } },
  }),
];
